import { useEffect, useState, type FormEvent } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import {
  Field,
  NumberInput,
  Select,
  Switch,
} from "../../design-system/form.js";
import { InlineConfirm } from "../../design-system/inline-confirm.js";
import { Modal } from "../../design-system/modal.js";
import { Notice } from "../../design-system/surfaces.js";
import { DAY_ABBREVIATIONS, timeToSeconds, trimTime } from "./grid-model.js";
import type { Playlist } from "../../api/types.js";

/**
 * One stretch of the week, and which playlist governs it.
 *
 * An end at or before the start runs through midnight — that is the brain's rule
 * (`findActiveSlot`), not a convention invented here, so the form says so rather than
 * refusing it. Where two slots cover the same hour the higher priority governs; the
 * lower one is what the feed falls back to when the first has nothing to play, which
 * is why overlapping is allowed and drawn rather than prevented.
 */
export interface SlotDraft {
  id?: string;
  playlistId: string;
  daysOfWeek: number[];
  startsAt: string;
  endsAt: string;
  priority: number;
  enabled: boolean;
}

export function SlotDialog({
  onClose,
  onDelete,
  onSave,
  open,
  playlists,
  slot,
}: {
  onClose: () => void;
  onDelete: (slotId: string) => void;
  onSave: (draft: SlotDraft) => void;
  open: boolean;
  playlists: readonly Playlist[];
  /** The slot being changed, or the seed of a new one clicked out of the grid. */
  slot: SlotDraft | null;
}) {
  const [playlistId, setPlaylistId] = useState("");
  const [days, setDays] = useState<number[]>([]);
  const [startsAt, setStartsAt] = useState("06:00");
  const [endsAt, setEndsAt] = useState("10:00");
  const [priority, setPriority] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !slot) return;
    setPlaylistId(slot.playlistId);
    setDays(slot.daysOfWeek);
    setStartsAt(trimTime(slot.startsAt));
    setEndsAt(trimTime(slot.endsAt));
    setPriority(String(slot.priority));
    setEnabled(slot.enabled);
    setError(null);
  }, [open, slot]);

  if (!slot) return null;

  const available = playlists.filter((playlist) => playlist.enabled);
  const startSeconds = timeToSeconds(startsAt);
  const endSeconds = timeToSeconds(endsAt);
  const overnight =
    startSeconds !== null && endSeconds !== null && endSeconds <= startSeconds;
  const ready =
    playlistId !== "" &&
    days.length > 0 &&
    startSeconds !== null &&
    endSeconds !== null;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) {
      setError(
        "A slot needs a playlist, at least one day, and two readable times.",
      );
      return;
    }
    onSave({
      ...(slot?.id === undefined ? {} : { id: slot.id }),
      playlistId,
      daysOfWeek: [...days].sort((left, right) => left - right),
      startsAt,
      endsAt,
      priority: Math.trunc(Number(priority) || 0),
      enabled,
    });
    onClose();
  }

  return (
    <Modal
      description="Which playlist plays when, in this schedule's timezone."
      onClose={onClose}
      open={open}
      title={slot.id ? "Edit slot" : "New slot"}
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <Field htmlFor="slot-playlist" label="Playlist">
          <Select
            id="slot-playlist"
            onChange={(event) => {
              setPlaylistId(event.target.value);
            }}
            required
            value={playlistId}
          >
            <option value="">Choose a playlist</option>
            {available.map((playlist) => (
              <option key={playlist.id} value={playlist.id}>
                {playlist.name}
              </option>
            ))}
          </Select>
        </Field>

        <fieldset className="min-w-0 border-0 p-0">
          <legend className="font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase">
            Days
          </legend>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {DAY_ABBREVIATIONS.map((label, day) => {
              const on = days.includes(day);
              return (
                <button
                  key={label}
                  aria-pressed={on}
                  className={cn(
                    "min-h-9 min-w-11 rounded-sm border px-2 text-xs font-medium transition-colors",
                    on
                      ? "border-transparent bg-accent text-accent-ink"
                      : "border-border-hi bg-surface text-ink-2 hover:border-accent-line",
                  )}
                  onClick={() => {
                    setDays((current) =>
                      current.includes(day)
                        ? current.filter((entry) => entry !== day)
                        : [...current, day],
                    );
                  }}
                  type="button"
                >
                  {label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="slot-starts" label="Starts">
            <input
              className="min-h-11 w-full rounded-sm border border-border bg-bg-2 px-3 font-mono text-sm text-ink transition-colors hover:border-border-hi focus:border-accent-line focus:outline-none"
              id="slot-starts"
              onChange={(event) => {
                setStartsAt(event.target.value);
              }}
              required
              type="time"
              value={startsAt}
            />
          </Field>
          <Field
            hint={
              overnight
                ? "Ends the next day: this slot runs through midnight."
                : undefined
            }
            htmlFor="slot-ends"
            label="Ends"
          >
            <input
              className="min-h-11 w-full rounded-sm border border-border bg-bg-2 px-3 font-mono text-sm text-ink transition-colors hover:border-border-hi focus:border-accent-line focus:outline-none"
              id="slot-ends"
              onChange={(event) => {
                setEndsAt(event.target.value);
              }}
              required
              type="time"
              value={endsAt}
            />
          </Field>
        </div>

        <Field
          hint="If slots overlap, the higher number wins."
          htmlFor="slot-priority"
          label="Priority"
        >
          <NumberInput
            id="slot-priority"
            onChange={(event) => {
              setPriority(event.target.value);
            }}
            step={1}
            value={priority}
          />
        </Field>

        <Switch
          checked={enabled}
          description="Off: this slot is ignored."
          label="Enabled"
          onChange={setEnabled}
        />

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          {slot.id ? (
            <InlineConfirm
              confirmLabel="Delete slot"
              onConfirm={() => {
                if (slot.id) onDelete(slot.id);
                onClose();
              }}
              question="Delete this slot?"
              trigger={(openConfirm) => (
                <Button onClick={openConfirm} size="small" variant="ghost">
                  <Trash2 aria-hidden="true" />
                  Delete
                </Button>
              )}
            >
              Every feed assigned this schedule stops using it at these hours.
            </InlineConfirm>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button onClick={onClose} variant="ghost">
              Cancel
            </Button>
            <Button disabled={!ready} type="submit">
              {slot.id ? "Save slot" : "Add slot"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
